/**
 * 営業デモ専用シナリオの配線（コミット対象・本番 build からは既定で除外）。
 *
 * ## 本番隔離の仕組み（NR1 local.ts と同じ流儀・ただしフラグで opt-in できる点が違い）
 * - index.ts / collection/zukanManifest.ts は literal glob（import.meta.glob('./demo.ts')）で
 *   このモジュールを吸収する（demo.ts が無くても壊れない）。
 * - `vite build` では apps/player/vite.config.ts の `exclude-demo-scenarios` plugin が
 *   このモジュールを空スタブ（default={} / demoZukanEntries=[]）へ差し替えるため、
 *   **フラグなしの本番 dist にはシナリオ本文・図鑑枠とも 1 バイトも混入しない**。
 * - 環境変数 `VITE_INCLUDE_DEMO=1` を立てて build したときだけ plugin が外れ、
 *   デモ同梱バンドルになる。vite.config.ts により出力先も `dist-demo` へ分離される
 *   （例: PowerShell `$env:VITE_INCLUDE_DEMO='1'; npm run build:player`）。
 * - `vite dev`（ローカル開発）では plugin が効かないため常に遊べる:
 *   http://localhost:5174/?scenario=demo-kiratta-library
 *
 * ## 収録シナリオ
 * - `demo-kiratta-library`: 『としょかんの こえ』（7/28 甲賀市営業デモ用・企画書
 *   docs/biz/demo_kiratta/demo_scenario_concept.md が仕様の正）
 * - `demo-jichitai`: 『潮待ち駅、17時40分』（自治体向け自己完結デモ・企画書
 *   docs/biz/demo_municipality/municipality_handout_demo_concept.md が仕様の正）
 * - `demo-koka-totono`: 『16時、名前のない企画書』（甲賀市・都市基盤整備課向け
 *   2サイクルデモ。企画書 docs/biz/demo_koka_cityhall_2026-07-29/
 *   totono_scenario_concept.md が仕様の正）
 *
 * ## アセットの差し替え手順（としょかんの こえ）
 * JSON = docs/biz/demo_kiratta/demo_scenario_kiratta_library.json は validator で検証できる
 * color 背景の正本として**不変**。画像はこのファイル内のランタイムパッチ（下記
 * kirattaBgPatches）が `src/scenarios/demoAssets/kirattaLibrary/*.svg` を注入する。
 * 1. 正式画像の反映: `node scripts/gen-demo-assets.mjs`（GEMINI_API_KEY 必要）が
 *    demoAssets/kirattaLibrary/ の同名 .svg を「生成画像を base64 内包した SVG ラッパー」で
 *    上書きする。import パス・本ファイル・JSON とも無変更で反映される。
 *    - bg_gaikan.svg=図書館外観・夕方 / bg_keijiban.svg=入口の掲示板 /
 *      bg_kodomo.svg=児童書コーナー・昼 / bg_tana388.svg=書架の谷間・388の棚 /
 *      fumi_standing.svg=フミ立ち絵（透過）
 * 2. フミ立ち絵は discovery_dialog の portraits.rightSrc と ending_good の
 *    tsukumogamiData.illustrationAssetId（図鑑カード）の両方へパッチされる。
 * 3. BGM（任意）: 無音で成立が正（企画書 §7）。入れる場合は scene/dialog の `bgm` へ。
 * 4. JSON 各所の `notes` の `[NOTE: PLACEHOLDER — ...]` は `[NOTE` 始まりでないと
 *    SceneView が notes を画面に表示してしまう点に注意（StepLayoutRenderer.tsx の isDebugNote 判定）。
 */
import { defaultLayout, type ScenarioContent } from 'shared';
import type { ZukanManifestEntry } from '../collection/zukanManifest';
import demoKirattaLibraryText from '../../../../docs/biz/demo_kiratta/demo_scenario_kiratta_library.json?raw';
// としょかんの こえ 用アセット。demo.ts からのみ import する＝フラグなし build では
// exclude-demo-scenarios plugin により demo.ts ごとモジュールグラフから消え、dist に混入しない。
// 現在は仮SVG（暖色グラデーション）。正式画像は scripts/gen-demo-assets.mjs が同名 .svg を
// 「生成画像を base64 内包した SVG ラッパー」で上書きする（import パス不変・ビルド機構無変更）。
// `?url&no-inline`: 仮SVGは 4KB 未満のため既定ではビルド時に data: URI へインライン化される。
// data: URI は resolveMediaRefToUrl（http(s)/UUID のみ受理）が拒否し図鑑イラストが
// プレースホルダへ落ちるため、常にファイルとして出力させ http(s) URL を保証する。
import demoKirattaBgGaikanAsset from './demoAssets/kirattaLibrary/bg_gaikan.svg?url&no-inline';
import demoKirattaBgKeijibanAsset from './demoAssets/kirattaLibrary/bg_keijiban.svg?url&no-inline';
import demoKirattaBgKodomoAsset from './demoAssets/kirattaLibrary/bg_kodomo.svg?url&no-inline';
import demoKirattaBgTana388Asset from './demoAssets/kirattaLibrary/bg_tana388.svg?url&no-inline';
import demoKirattaFumiStandingAsset from './demoAssets/kirattaLibrary/fumi_standing.svg?url&no-inline';
import demoMunicipalityHandoutText from '../../../../docs/biz/demo_municipality/demo_scenario_municipality_handout.json?raw';
import demoStationPlatformAsset from '../../../../docs/biz/demo_municipality/assets/shiomachi_station_platform_v1.png?url';
import demoStationWaitingAsset from '../../../../docs/biz/demo_municipality/assets/shiomachi_station_waiting_room_v1.png?url';
import demoStationRevealAsset from '../../../../docs/biz/demo_municipality/assets/shiomachi_station_reveal_v1.png?url';
import demoMioPortraitAsset from '../../../../docs/biz/demo_municipality/assets/mio_portrait_v1.png?url';
import demoKokaTotonoText from '../../../../docs/biz/demo_koka_cityhall_2026-07-29/demo_scenario_koka_totono.json?raw';
import demoKokaCorridorAsset from '../../../../docs/biz/demo_koka_cityhall_2026-07-29/assets/cityhall_corridor_clean_v4.png?url';
import demoKokaProposalAsset from '../../../../docs/biz/demo_koka_cityhall_2026-07-29/assets/totono_mystery_proposal_v1.svg?url';
import demoTotonoPortraitAsset from '../../../../docs/biz/demo_koka_cityhall_2026-07-29/assets/totono_portrait_v1.png?url';
import demoTotonoZukanAsset from '../../../../docs/biz/demo_koka_cityhall_2026-07-29/assets/totono_zukan_v1.png?url';

/** 『としょかんの こえ』（キラッタ営業デモ）本体。 */
export const demoKirattaLibrary: ScenarioContent = JSON.parse(
  demoKirattaLibraryText
) as ScenarioContent;

/** Viteの相対asset URLを、MediaRefSchemaが許可する実行時の絶対URLへ変換する。 */
function toRuntimeAssetUrl(assetUrl: string): string {
  const origin = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
  return new URL(assetUrl, origin).href;
}

// ---------------------------------------------------------------------------
// としょかんの こえ: アセット注入パッチ
// raw JSON は単体 validator で検証できる color 背景（正本・不変）を持ち、デモ配線時だけ
// ランタイムで画像へ差し替える（潮待ち駅・トトノと同じ流儀）。
// dimOpacity は「字幕face（枠なし・すそ消し）の可読性」優先で選定:
//   scene（テキストoverlay直乗せ）= 0.25 / dialog = 0.30 / 文量最大の after_gate = 0.35。
// 仮SVG背景は #1c1410 系に馴染む暗めの暖色のため、この帯で可読（実走確認済み）。
// 正式画像（生成）が明るい場合は再調整の余地あり。
// ---------------------------------------------------------------------------
const kirattaBgGaikanUrl = toRuntimeAssetUrl(demoKirattaBgGaikanAsset);
const kirattaBgKeijibanUrl = toRuntimeAssetUrl(demoKirattaBgKeijibanAsset);
const kirattaBgKodomoUrl = toRuntimeAssetUrl(demoKirattaBgKodomoAsset);
const kirattaBgTana388Url = toRuntimeAssetUrl(demoKirattaBgTana388Asset);
const kirattaFumiStandingUrl = toRuntimeAssetUrl(demoKirattaFumiStandingAsset);

/** stepId → 差し替える背景（src と dimOpacity）。 */
const kirattaBgPatches: Record<string, { src: string; dimOpacity: number }> = {
  intro_scene: { src: kirattaBgGaikanUrl, dimOpacity: 0.25 },
  omen_dialog: { src: kirattaBgKeijibanUrl, dimOpacity: 0.3 },
  library_scene: { src: kirattaBgKodomoUrl, dimOpacity: 0.25 },
  voice_contact: { src: kirattaBgKodomoUrl, dimOpacity: 0.3 },
  voice_response: { src: kirattaBgKodomoUrl, dimOpacity: 0.3 },
  after_gate: { src: kirattaBgKodomoUrl, dimOpacity: 0.35 },
  discovery_dialog: { src: kirattaBgTana388Url, dimOpacity: 0.3 }
};

for (const node of demoKirattaLibrary.nodes) {
  for (const step of node.steps ?? []) {
    const bgPatch = kirattaBgPatches[step.id];
    if (bgPatch) {
      (step as any).bg = {
        kind: 'image',
        src: bgPatch.src,
        fit: 'cover',
        dimOpacity: bgPatch.dimOpacity
      };
    }
    // フミ立ち絵: JSON 内の仮 SVG data URI をファイル化したアセットへ差し替え
    // （正式立ち絵が入ったら gen スクリプトが同名ファイルを上書きするだけで反映される）。
    if (step.id === 'discovery_dialog' && step.type === 'dialog') {
      (step as any).portraits = { rightSrc: kirattaFumiStandingUrl };
    }
    // 図鑑イラスト: resolveMediaRefToUrl は http(s) URL をそのまま受けるため
    // illustrationAssetId に実行時 URL を配線できる（demo-koka-totono と同じ流儀）。
    if (step.id === 'ending_good' && step.type === 'ending') {
      const data = (step as any).tsukumogamiData;
      if (data) data.illustrationAssetId = kirattaFumiStandingUrl;
    }
  }
}

/** 『潮待ち駅、17時40分』（自治体向け自己完結デモ）本体。 */
const stationPlatformUrl = toRuntimeAssetUrl(demoStationPlatformAsset);
const stationWaitingUrl = toRuntimeAssetUrl(demoStationWaitingAsset);
const stationRevealUrl = toRuntimeAssetUrl(demoStationRevealAsset);
const mioPortraitUrl = toRuntimeAssetUrl(demoMioPortraitAsset);
export const demoMunicipalityHandout: ScenarioContent = JSON.parse(
  demoMunicipalityHandoutText
    .replaceAll('__DEMO_STATION_PLATFORM_URL__', stationPlatformUrl)
    .replaceAll('__DEMO_STATION_WAITING_URL__', stationWaitingUrl)
) as ScenarioContent;

// raw JSONは単体validatorで検証できるcolor背景を持たせ、デモ配線時だけ専用画像へ差し替える。
const stationPlatformStepIds = new Set([
  'station_awakening_scene',
  'station_meeting_dialog',
  'station_loop_scene'
]);
const stationWaitingStepIds = new Set([
  'station_memory_dialog',
  'station_inference_bridge_dialog',
  'station_inference_retry_dialog',
  'station_bond_memory_dialog',
  'station_deduction_dialog',
  'station_wrong_route_dialog',
  'station_wrong_departure_dialog',
  'station_bell_dialog'
]);
const stationRevealStepIds = new Set([
  'station_reveal_scene',
  'station_manifestation_dialog',
  'station_okuri_understanding_dialog',
  'station_reveal_dialog',
  'station_escape_dialog',
  'station_resolution_dialog'
]);
const mioPortraitStepIds = new Set([
  'station_meeting_dialog',
  'station_memory_dialog',
  'station_inference_bridge_dialog',
  'station_inference_retry_dialog',
  'station_bond_memory_dialog',
  'station_deduction_dialog',
  'station_wrong_route_dialog',
  'station_wrong_departure_dialog',
  'station_bell_dialog',
  'station_manifestation_dialog',
  'station_okuri_understanding_dialog',
  'station_reveal_dialog',
  'station_escape_dialog',
  'station_resolution_dialog'
]);
const stationRevealPortraitStepIds = new Set([
  'station_manifestation_dialog',
  'station_okuri_understanding_dialog',
  'station_reveal_dialog',
  'station_escape_dialog',
  'station_resolution_dialog'
]);
const opaqueMioDialogLayout = (() => {
  const layout = defaultLayout('dialog_overlay');
  const params = layout.params as any;
  return {
    ...layout,
    params: {
      ...params,
      speakerFocus: {
        ...(params.speakerFocus ?? {}),
        enabled: false
      }
    }
  };
})();

for (const node of demoMunicipalityHandout.nodes) {
  for (const step of node.steps ?? []) {
    if (stationPlatformStepIds.has(step.id)) {
      (step as any).bg = {
        kind: 'image',
        src: stationPlatformUrl,
        fit: 'cover',
        dimOpacity: step.type === 'scene' ? 0.16 : 0.46
      };
    }
    if (stationWaitingStepIds.has(step.id)) {
      (step as any).bg = {
        kind: 'image',
        src: stationWaitingUrl,
        fit: 'cover',
        dimOpacity: 0.48
      };
    }
    if (stationRevealStepIds.has(step.id)) {
      (step as any).bg = {
        kind: 'image',
        src: stationRevealUrl,
        fit: 'cover',
        dimOpacity: step.type === 'scene' ? 0.12 : 0.44
      };
    }
    if (mioPortraitStepIds.has(step.id) && step.type === 'dialog') {
      (step as any).portraits = { rightSrc: mioPortraitUrl };
      // 字幕用の汎用「すそ消し」は、焼き込み文字のない澪の全身立ち絵には不要。
      // 元PNGの靴まで不透明に保ち、幽霊のような下端透過を防ぐ。
      (step as any).portraitBottomFade = false;
      // 選択待ちでは主人公が最終話者になるため、汎用の非話者45%化も幽霊らしさを生む。
      // このデモの澪は全会話で不透明に保つ。
      (step as any).layout = opaqueMioDialogLayout;
      const isRevealScene = stationRevealPortraitStepIds.has(step.id);
      (step as any).portraitPositions = {
        right: {
          xPct: 77,
          yPct: isRevealScene ? 49 : 47,
          scale: 1,
          widthPct: isRevealScene ? 44 : 46,
          heightPct: isRevealScene ? 70 : 78,
          fit: 'contain_landscape',
          cropXPct: 50,
          cropYPct: 50
        }
      };
    }
  }
}

/** 『16時、名前のない企画書』（甲賀市・都市基盤整備課向け2サイクルデモ）本体。 */
const kokaCorridorUrl = toRuntimeAssetUrl(demoKokaCorridorAsset);
const kokaProposalUrl = toRuntimeAssetUrl(demoKokaProposalAsset);
const totonoPortraitUrl = toRuntimeAssetUrl(demoTotonoPortraitAsset);
const totonoZukanUrl = toRuntimeAssetUrl(demoTotonoZukanAsset);
export const demoKokaTotono: ScenarioContent = JSON.parse(
  demoKokaTotonoText
    .replaceAll('__DEMO_KOKA_CORRIDOR_URL__', kokaCorridorUrl)
    .replaceAll('__DEMO_KOKA_PROPOSAL_URL__', kokaProposalUrl)
    .replaceAll('__DEMO_TOTONO_PORTRAIT_URL__', totonoPortraitUrl)
    .replaceAll('__DEMO_TOTONO_ZUKAN_URL__', totonoZukanUrl)
) as ScenarioContent;

const totonoPortraitStepIds = new Set([
  'totono_reveal_dialog',
  'totono_resolution_dialog'
]);
const opaqueTotonoDialogLayout = (() => {
  const layout = defaultLayout('dialog_overlay');
  const params = layout.params as any;
  return {
    ...layout,
    params: {
      ...params,
      speakerFocus: {
        ...(params.speakerFocus ?? {}),
        enabled: false
      }
    }
  };
})();

for (const node of demoKokaTotono.nodes) {
  for (const step of node.steps ?? []) {
    // raw JSON は単体 validator 用に color 背景を持ち、デモ配線時だけ実景へ差し替える。
    if (step.type === 'scene' || step.type === 'dialog') {
      (step as any).bg = {
        kind: 'image',
        src: kokaCorridorUrl,
        fit: 'cover',
        dimOpacity: step.type === 'scene' ? 0.16 : (step as any).bg?.dimOpacity ?? 0.42
      };
    }
    if (totonoPortraitStepIds.has(step.id) && step.type === 'dialog') {
      (step as any).portraits = { leftSrc: totonoPortraitUrl };
      (step as any).portraitBottomFade = false;
      (step as any).layout = opaqueTotonoDialogLayout;
      (step as any).portraitPositions = {
        left: {
          xPct: 39,
          yPct: 48,
          scale: 1,
          widthPct: 78,
          heightPct: 72,
          fit: 'contain',
          cropXPct: 50,
          cropYPct: 50
        }
      };
    }
  }
}

/**
 * 図鑑マニフェストのデモ分エントリ（zukanManifest.ts が glob で吸収）。
 * key は utils/collection.ts の resolveKey 規約（EndingView は scenarioId を渡さない
 * ため `name:<tsukumogamiData.name>`）と一致させること。
 */
export const demoZukanEntries: ZukanManifestEntry[] = [
  {
    // としょかんの こえ（営業デモ・?scenario=demo-kiratta-library）
    // key: ending step `ending_good` の tsukumogamiData.name（scenarioId 無し記録＝ name キー）。
    key: 'name:フミ（本）',
    scenarioIds: ['demo-kiratta-library'],
    rarity: 'rare',
    hint: 'としょかんの くらい棚で、小さな声がしている。',
    declaredEndingCount: 1
  },
  {
    // 潮待ち駅、17時40分（自治体向け自己完結デモ・?scenario=demo-jichitai）
    // key: ending step `station_ending_good` の tsukumogamiData.name。
    key: 'name:オクリ（旧潮待駅の発車時刻表）',
    scenarioIds: ['demo-jichitai'],
    rarity: 'uncommon',
    description:
      '旧潮待駅で幼い澪の挨拶と父の発車ベルを毎日受け止めてきた発車時刻表の付喪神。17年前に途切れた最後の見送りを、17時40分の記憶とともに待ち続けていた。',
    hint: '海辺の旧駅で、17時40分だけが止まっている。',
    declaredEndingCount: 1
  },
  {
    // 16時、名前のない企画書（甲賀市・都市基盤整備課向け2サイクルデモ）
    // key: ending step `totono_ending_good` の tsukumogamiData.name。
    key: 'name:トトノ（都市基盤整備課）',
    scenarioIds: ['demo-koka-totono'],
    rarity: 'rare',
    description:
      '執務室、窓口、案内板、記録、そこで積み重なった対話を器に生まれた都市基盤整備課そのものの付喪神。出所不明の企画書を作り、自分の作者名と、人が確認して決めることの大切さを学んだ。',
    hint: '16時、共有テーブルに作成者不明の企画書が置かれている。',
    declaredEndingCount: 1
  }
];

export default {
  // キー = `?scenario=` の URL パラメータ名（進行保存キー player_progress_<id> にもなる）
  'demo-kiratta-library': demoKirattaLibrary,
  'demo-jichitai': demoMunicipalityHandout,
  'demo-koka-totono': demoKokaTotono
} satisfies Record<string, ScenarioContent>;
